import { Fragment as _Fragment, jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { MCPanelHeader } from './MCSection';
const KIND_LABEL = {
    missing_receipt: 'MISSING RECEIPT',
    receipt_review: 'RECEIPT REVIEW',
    ocr_stuck: 'OCR STUCK',
    pi_unreachable: 'OCR PI OFFLINE',
    unpaid_past_end: 'UNPAID · BOOKING ENDED',
    paid_not_completed: 'PAID · NOT COMPLETED',
    clock_open_overnight: 'CLOCK OPEN OVERNIGHT',
    checklist_overdue: 'CHECKLIST OVERDUE',
};
function formatRelative(iso, now) {
    const t = new Date(iso).getTime();
    const diffMs = Math.max(0, now - t);
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1)
        return 'just now';
    if (mins < 60)
        return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24)
        return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}
/**
 * Shared renderer for attention items. Used by MCAttentionBell's popover
 * and the wallboard Attention panel — single source of truth for row
 * markup, empty state, and relative-time ticking.
 */
export function MCAttentionList({ items, readIds, onMarkRead, onOpenItem, heading, listClassName = 'max-h-[60vh] overflow-y-auto', hideHeader = false, headingId, }) {
    const [nowTick, setNowTick] = useState(() => Date.now());
    useEffect(() => {
        const id = window.setInterval(() => setNowTick(Date.now()), 30000);
        return () => window.clearInterval(id);
    }, []);
    const read = readIds ?? new Set();
    const isIdle = items.length === 0;
    const defaultHeading = (_jsxs(_Fragment, { children: ["// ATTENTION", items.length > 0 ? ` · ${items.length} OPEN` : ''] }));
    const scrollClassName = `${listClassName} mc-scroll-thin`;
    return (_jsxs(_Fragment, { children: [!hideHeader && (_jsx(MCPanelHeader, { label: heading ?? defaultHeading, headingId: headingId, as: "div", border: true, flush: true, className: "px-4 py-3" })), isIdle ? (_jsxs("div", { className: "flex flex-col items-center justify-center py-12 gap-2 flex-1", children: [_jsx("span", { className: "inline-flex items-center justify-center w-8 h-8 rounded-full", style: { border: '1px solid var(--mc-cyan)', color: 'var(--mc-cyan)' }, children: _jsx(Check, { className: "w-4 h-4", strokeWidth: 2 }) }), _jsx("span", { className: "mc-mono text-[12px]", style: { color: 'var(--mc-text-meta)' }, children: "All clear." })] })) : (_jsx("ul", { role: "list", "aria-live": "polite", className: scrollClassName, children: items.map((item, idx) => {
                    const isRead = read.has(item.id);
                    const pipColor = item.severity === 'red' ? 'var(--mc-magenta)' : 'var(--mc-amber)';
                    return (_jsx("li", { role: "listitem", className: "mc-attn-row", style: {
                            borderTop: idx === 0 ? 'none' : '1px solid var(--mc-divider-soft)',
                            opacity: isRead ? 0.6 : 1,
                        }, children: _jsxs("button", { type: "button", onClick: () => {
                                if (!isRead)
                                    onMarkRead?.(item.id);
                                onOpenItem?.(item);
                            }, className: "w-full text-left px-4 py-3 flex gap-3 items-stretch transition-colors focus:outline-none focus-visible:bg-[color:var(--mc-bg-raised)]", children: [_jsx("span", { "aria-hidden": true, className: "flex-shrink-0 w-[3px] rounded-full self-stretch", style: { background: pipColor, opacity: isRead ? 0.4 : 1 } }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsxs("div", { className: "flex items-center justify-between gap-2 mb-1", children: [_jsx("span", { className: "mc-mono text-[10px] uppercase tracking-[0.12em]", style: { color: pipColor }, children: KIND_LABEL[item.kind] }), _jsx("span", { className: "mc-mono text-[11px] flex-shrink-0", style: { color: 'var(--mc-text-meta-dim)' }, children: formatRelative(item.createdAt, nowTick) })] }), _jsx("div", { className: "mc-mono text-[13px] mb-0.5 truncate", style: { color: 'var(--mc-text-primary)' }, children: item.title }), _jsx("div", { className: "text-[12px] leading-snug", style: { color: 'var(--mc-text-meta)' }, children: item.detail })] })] }) }, item.id));
                }) }))] }));
}
